import { IPauseAdProvider } from '../interfaces/IPauseAdProvider';
import { IPauseAdRequestParams } from '../interfaces/IPauseAdRequestParams';
import { IPauseAdResult } from '../interfaces/IPauseAdResult';

/**
 * @copyright © Paramount Streaming
 * @description Google Ad Manager (GAM) pause ad provider.
 *
 * Implements `IPauseAdProvider` for the GAM DoubleClick companion ad format.
 *
 * **Note:** GAM pause ads are implemented but **not used in production**.
 * FreeWheel is the active provider. GAM support is retained for flexibility.
 *
 * **URL construction** (`buildUrl`):
 * Flattens all param groups into a standard `?key=value&key=value` query string
 * on the GAM `gampad/adx` endpoint. In practice, Smart Tag typically passes a
 * pre-built URL for GAM requests via the `request.url` override, so `buildUrl`
 * serves as a fallback for structured-param callers.
 *
 * **Response parsing** (`parseResponse`):
 * Extracts creative data from the GAM VAST companion ad format:
 * - `Companion[width="848"]` as the root element (848px pause ad slot)
 * - `StaticResource[creativeType*="image/"]` for the image URL
 * - `CompanionClickThrough` for the click destination
 * - `Impression` elements for impression tracking pixels
 */
export class GamPauseAdProvider implements IPauseAdProvider {
	/** @inheritdoc */
	readonly id = 'gam';

	/**
	 * @description Builds a GAM ad call URL by flattening all param groups into a single query string.
	 *
	 * @param params - Structured parameter groups. All groups are merged (global -> merged -> slot).
	 * @returns Fully qualified GAM DoubleClick URL.
	 */
	buildUrl( params: IPauseAdRequestParams ): string {
		const base = 'https://pubads.g.doubleclick.net/gampad/adx';
		const all = {
			...( params?.global ?? {} ),
			...( params?.merged ?? {} ),
			...( params?.slot ?? {} )
		};

		const qs = Object.entries( all )
			.filter( ( [ , v ] ) => v != null && v !== '' )
			.map( ( [ k, v ] ) => `${encodeURIComponent( k )}=${encodeURIComponent( String( v ) )}` )
			.join( '&' );

		return qs ? `${base}?${qs}` : base;
	}

	/**
	 * @description Parses a GAM VAST XML response into a normalized `IPauseAdResult`.
	 *
	 * GAM uses a companion ad format for pause ads. The parser targets:
	 * - `<Companion width="848">` -- the 848px-wide pause ad slot
	 * - `<StaticResource creativeType="image/...">` -- the creative image URL
	 * - `<CompanionClickThrough>` -- click destination URL
	 *
	 * Case-insensitive fallback selectors handle non-standard capitalization.
	 *
	 * @param xml - Parsed VAST XML Document.
	 * @returns Normalized pause ad result.
	 */
	parseResponse( xml: Document ): IPauseAdResult {
		let imageSource: string | null = null;
		let clickThrough: string | null = null;

		const rootNode =
			xml.querySelector( 'Companion[width="848"]' )
			??
			xml.querySelector( 'companion[width="848"]' );

		if ( rootNode ) {
			const image =
				rootNode.querySelector( 'StaticResource[creativeType*="image/"]' )
				??
				rootNode.querySelector( 'staticresource[creativeType*="image/"]' );

			imageSource = image?.textContent?.trim() || null;

			const clickURL =
				rootNode.querySelector( 'CompanionClickThrough' )
				??
				rootNode.querySelector( 'companionclickthrough' );

			clickThrough = clickURL?.textContent?.trim() || null;
		}

		return {
			imageSource,
			clickThrough,
			success: !!imageSource,
			clickTrackingURLs: [],
			impressions: GamPauseAdProvider.#extractImpressions( xml )
		};
	}

	/**
	 * @description Collects all `<Impression>` tracking URLs from the VAST document.
	 */
	static #extractImpressions( xml: Document ): string[] {
		const urls: string[] = [];
		const nodes = xml.querySelectorAll( 'Impression' );
		nodes.forEach( ( node ) => {
			const content = node?.firstChild?.textContent?.trim();
			if ( content ) {
				urls.push( content );
			}
		} );
		return urls;
	}
}